import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi } from "../../../hooks/useApi";
import { useAuth } from "../../../contexts/AuthContext";
import { attentionTest, buildInsightContext, ATTENTION_KEYS } from "../screens/team/teamInsights";

const DEFAULT_PAGE_SIZE = 12;

// Owns Team Management server state — mirrors the legacy Team page contracts.
export function useTeamData() {
  const api = useApi();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE); // selectable (legacy parity)
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [desktopAgent, setDesktopAgent] = useState(""); // "" | "yes" | "no" | "not_active"
  // Members default to active only. The backend applies no status guard, so
  // without this archived members — and soft-deleted ones, which are stored as
  // "inactive" — show up in the list and inflate every headcount.
  const [status, setStatus] = useState("active"); // "all" | active | inactive | archived | pending
  // Quick-filter from the pulse panel — "" | pending | noagent | nomanager | noposition.
  // These have no server-side parameter, so they resolve against `allUsers`.
  const [attention, setAttentionRaw] = useState("");

  const [allUsers, setAllUsers] = useState([]);   // for stats + reporting-manager picker
  const [allLoading, setAllLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [structure, setStructure] = useState([]); // [{ level, positions: [{roleName}] }]

  const fetchUsers = useCallback(async () => {
    // With an attention filter on, the list is derived from `allUsers` below;
    // the paged endpoint can't answer these questions, so don't ask it.
    if (attention) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (search.trim()) params.append("search", search.trim());
      if (role) params.append("role", role);
      if (desktopAgent) params.append("desktopAgent", desktopAgent);
      if (status && status !== "all") params.append("status", status);
      const res = await api.get(`/api/v1/users?${params.toString()}`);
      const d = res.data?.data || {};
      setUsers(d.users || []);
      setPages(d.pagination?.pages || 1);
      setTotal(d.pagination?.total || 0);
    } catch (_) { setUsers([]); }
    finally { setLoading(false); }
  }, [api, page, pageSize, search, role, desktopAgent, status, attention]);

  const fetchAll = useCallback(async () => {
    setAllLoading(true);
    try {
      // Fast path: stealth Desktop Agent flag only (no screenshot aggregation)
      const res = await api.get("/api/v1/users/all");
      setAllUsers(res.data?.data?.users || []);
    } catch (_) {}
    finally { setAllLoading(false); }
  }, [api]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get("/api/v1/organizations/roles");
      const list = (res.data?.data?.roles || []).filter((r) => r.value !== "main_admin");
      setRoles(list);
    } catch (_) {
      setRoles([{ value: "org_admin", label: "Admin" }, { value: "org_user", label: "Member" }]);
    }
  }, [api]);

  const fetchStructure = useCallback(async () => {
    try {
      const res = await api.get("/api/v1/organizations/team-structure");
      setStructure(res.data?.data?.teamStructure || []);
    } catch (_) {}
  }, [api]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchAll(); fetchRoles(); fetchStructure(); }, [fetchAll, fetchRoles, fetchStructure]);
  useEffect(() => { setPage(1); }, [search, role, pageSize, desktopAgent, status, attention]);

  // "Not active today" is itself an attention-shaped filter, and it's the one
  // set `allUsers` cannot reproduce (the server derives it from tracked hours).
  // Combining the two would silently show a list that ignores one of them, so
  // picking an attention chip drops it.
  const setAttention = useCallback((next) => {
    const key = ATTENTION_KEYS.includes(next) ? next : "";
    if (key && desktopAgent === "not_active") setDesktopAgent("");
    setAttentionRaw(key);
  }, [desktopAgent]);

  // ── Permission / visibility gating (parity with legacy Team) ──

  // Can the current user add members? Admins always; org_user needs addTeamMember.
  const canAddTeamMembers = useCallback(() => {
    if (user?.role === "main_admin" || user?.role === "org_admin") return true;
    if (user?.role === "org_user" && user?.permissions?.addTeamMember) return true;
    return false;
  }, [user]);

  // Restrict visible members for org_user to self + lower hierarchy levels.
  const canViewMember = useCallback((member) => {
    const currentUser = user;
    if (!currentUser || !member) return false;
    if (currentUser.role === "main_admin" || currentUser.role === "org_admin") return true;
    if (currentUser.role !== "org_user") return true;

    const normalize = (s) => (s || "").trim().toLowerCase();
    const currentLevel = structure.findIndex(
      (lvl) => lvl.positions?.some((p) => normalize(p.roleName) === normalize(currentUser.position))
    );
    const memberLevel = structure.findIndex(
      (lvl) => lvl.positions?.some((p) => normalize(p.roleName) === normalize(member.position))
    );
    const isSelf = (member._id?.toString && member._id?.toString() === currentUser.id) || (member.id === currentUser.id);
    if (currentLevel === -1 || memberLevel === -1) return isSelf;
    return isSelf || memberLevel > currentLevel;
  }, [user, structure]);

  // Eligible reporting managers for a given target position, honouring the
  // team-structure hierarchy (same rules as legacy getAvailableReportingManagers).
  const getAvailableReportingManagers = useCallback((selectedPosition, currentUserId = null) => {
    const norm = (s) => (s || "").trim();
    // org_user: self + lower-level positions only
    if (user?.role === "org_user") {
      let currentUserLevel = -1;
      for (let i = 0; i < structure.length; i++) {
        if (structure[i].positions?.some((pos) => norm(pos.roleName) === norm(user.position))) { currentUserLevel = i; break; }
      }
      if (currentUserLevel === -1) return [];
      const eligiblePositions = [];
      for (let i = currentUserLevel + 1; i < structure.length; i++) {
        (structure[i].positions || []).forEach((p) => eligiblePositions.push(p.roleName));
      }
      if (user.position) eligiblePositions.push(user.position);
      return allUsers.filter((u) => {
        if (currentUserId && u._id === currentUserId) return false;
        if (u.position && norm(u.position) === norm(user.position)) return u._id === user.id;
        return u.position && eligiblePositions.some((pos) => norm(pos) === norm(u.position));
      });
    }

    if (!selectedPosition || !structure || structure.length === 0) {
      return allUsers.filter((u) => {
        if (currentUserId && u._id === currentUserId) return false;
        return u.role === "org_admin" || u.position;
      });
    }

    let selectedLevel = -1;
    for (let i = 0; i < structure.length; i++) {
      if (structure[i].positions?.find((p) => norm(p.roleName) === norm(selectedPosition))) { selectedLevel = i; break; }
    }
    if (selectedLevel === -1) {
      return allUsers.filter((u) => {
        if (currentUserId && u._id === currentUserId) return false;
        return u.role === "org_admin";
      });
    }
    return allUsers.filter((u) => {
      if (currentUserId && u._id === currentUserId) return false;
      if (u.role === "org_admin") return true;
      if (u.position) {
        for (let i = 0; i <= selectedLevel; i++) {
          if (structure[i].positions?.find((p) => norm(p.roleName) === norm(u.position))) return true;
        }
      }
      return false;
    });
  }, [user, structure, allUsers]);

  // Stats over the SAME filter set as the list, so the strip moves when you
  // filter. /users/all can't be filtered server-side (it's shared with the
  // reporting-manager dropdown and cached org-wide), so the predicate is applied
  // to the copy we already have — no extra request.
  //
  // ponytail: desktopAgent "not_active" is a server-side set (zero tracked hours
  // today) that can't be reproduced here, so for that one filter the strip falls
  // back to the server's total and the breakdowns stay agent-unfiltered.
  const applyFilters = useCallback((list, { withStatus = true } = {}) => {
    const needle = search.trim().toLowerCase();
    return list.filter((u) => {
      if (withStatus && status !== "all" && (u.status || "active") !== status) return false;
      if (role && u.role !== role) return false;
      if (desktopAgent === "yes" && !u.hasDesktopAgent) return false;
      if (desktopAgent === "no" && u.hasDesktopAgent) return false;
      if (needle) {
        // Match the server's semantics exactly: it regex-tests each field on its
        // own, so "Ada Lovelace" matches nothing there. Joining the fields here
        // instead would let the panels count a member the list can't show.
        const fields = [u.firstName, u.lastName, u.email, u.stubEmail];
        if (!fields.some((f) => String(f || "").toLowerCase().includes(needle))) return false;
      }
      return true;
    });
  }, [status, role, desktopAgent, search]);

  const scoped = useMemo(() => applyFilters(allUsers), [allUsers, applyFilters]);

  // Same set with the status filter left off. The list defaults to Active, which
  // hides every pending member — so a "pending invite" count read off `scoped`
  // would always be zero. Status-backed panel rows are counted against this.
  const scopedAnyStatus = useMemo(
    () => applyFilters(allUsers, { withStatus: false }),
    [allUsers, applyFilters],
  );

  // Reporting relationships are read over the FULL org, never the filtered set —
  // otherwise "active only" turns a manager whose reports are all archived into
  // a false orphan.
  const insightContext = useMemo(() => buildInsightContext(allUsers), [allUsers]);

  // Attention filters ask questions the members endpoint has no parameter for
  // ("who has no manager?"). Running them over one server page would only ever
  // search 12 rows, so they resolve against the whole org list we already hold.
  const attentionUsers = useMemo(() => {
    const test = attention ? attentionTest(attention) : null;
    if (!test) return null;
    return scoped.filter((u) => test(u, insightContext)).filter((u) => canViewMember(u));
  }, [attention, scoped, insightContext, canViewMember]);

  // Visible list after applying org_user hierarchy visibility (parity).
  //
  // ponytail: on the server path this hides rows AFTER the server has paginated,
  // so an org_user can get a short page while `total` still counts members they
  // may not see. Reported via scopedOut so the UI can say so instead of showing a
  // range that doesn't add up; the real fix is scoping the query server-side.
  // The attention path doesn't have that problem — it filters the full list
  // before paginating, so its counts are exact.
  const serverVisible = users.filter((u) => canViewMember(u));

  const visibleUsers = attentionUsers
    ? attentionUsers.slice((page - 1) * pageSize, page * pageSize)
    : serverVisible;
  const listTotal = attentionUsers ? attentionUsers.length : total;
  const listPages = attentionUsers ? Math.max(1, Math.ceil(attentionUsers.length / pageSize)) : pages;
  const scopedOut = attentionUsers ? 0 : users.length - serverVisible.length;

  const stats = {
    total: desktopAgent === "not_active" ? total : (scoped.length || total),
    admins: scoped.filter((u) => u.role === "org_admin").length,
    members: scoped.filter((u) => u.role === "org_user").length,
    stealth: scoped.filter((u) => u.stealth_mode).length,
    desktopAgent: scoped.filter((u) => u.hasDesktopAgent).length,
    billingEnabled: scoped.filter((u) => u.billingEnabled !== false).length,
    archived: allUsers.filter((u) => (u.status || "active") === "archived").length,
  };

  const refresh = useCallback(() => { fetchUsers(); fetchAll(); }, [fetchUsers, fetchAll]);

  return {
    api, user, users: visibleUsers,
    // On the attention path the list comes from /users/all, so it's that request
    // the spinner belongs to.
    loading: attention ? allLoading : loading,
    page, pages: listPages, total: listTotal, setPage, scopedOut,
    pageSize, setPageSize,
    search, setSearch, role, setRole, desktopAgent, setDesktopAgent,
    status, setStatus, attention, setAttention,
    roles, allUsers, structure, stats, refresh,
    // Filtered org-wide sets + reporting context, for the pulse panels.
    scoped, scopedAnyStatus, insightContext,
    canAddTeamMembers, canViewMember, getAvailableReportingManagers,
  };
}
